import { p, h, ul, code, tip, info, warn, table, cards, ladder } from './helpers';

const course = {
  id: 'media-and-call-flows',
  step: 2,
  title: 'Media and Call Flows',
  tagline: 'SDP, RTP and the ladder diagrams behind registration, calls, cancels, holds and NAT.',
  icon: 'Workflow',
  colors: ['#00e5ff', '#3d5afe'],
  level: 'Beginner',
  lessons: [
    {
      id: 'sdp-offer-answer',
      title: 'SDP and the offer/answer model',
      icon: 'FileAudio',
      minutes: 10,
      summary: 'How two phones agree on codecs, IP addresses and ports before any audio flows.',
      blocks: [
        p('The **Session Description Protocol** (SDP, RFC 8866, originally RFC 4566) is a plain-text format that describes a media session. SIP carries it in the message body. The **offer/answer model** (RFC 3264) defines the negotiation: one side offers a list of codecs and an address, the other answers with the subset it accepts and its own address.'),
        code('sdp', `
v=0
o=alice 2890844526 2890844526 IN IP4 192.0.2.101
s=Talk
c=IN IP4 192.0.2.101
t=0 0
m=audio 49170 RTP/AVP 111 0 8 101
a=rtpmap:111 opus/48000/2
a=rtpmap:0 PCMU/8000
a=rtpmap:8 PCMA/8000
a=rtpmap:101 telephone-event/8000
a=fmtp:101 0-16
a=ptime:20
a=sendrecv`, 'An audio offer'),
        table(['Line', 'Meaning'], [
          ['`v=`', 'Protocol version, always 0.'],
          ['`o=`', 'Origin: username, session id, session version, network type, address type, address. The version must increase whenever the SDP changes.'],
          ['`s=`', 'Session name. Often just `-`.'],
          ['`c=`', 'Connection data: the IP address where this side wants to **receive** media.'],
          ['`t=`', 'Timing. `0 0` means unbounded.'],
          ['`m=`', 'Media line: type, receive port, transport profile and the list of payload types in order of preference.'],
          ['`a=rtpmap`', 'Maps a payload type number to a codec name and clock rate.'],
          ['`a=sendrecv`', 'Direction. Other values: `sendonly`, `recvonly`, `inactive`.'],
        ]),
        h('Payload types'),
        table(['PT', 'Codec', 'Notes'], [
          ['0', 'PCMU (G.711 u-law)', '64 kbit/s, North America and Japan'],
          ['8', 'PCMA (G.711 a-law)', '64 kbit/s, most of the rest of the world'],
          ['9', 'G.722', 'Wideband (HD voice). Its RTP clock rate is 8000 for historical reasons.'],
          ['18', 'G.729', 'Low bandwidth, 8 kbit/s'],
          ['96-127', 'Dynamic', 'Assigned by rtpmap, e.g. Opus, telephone-event'],
        ]),
        tip('DTMF', '`telephone-event` (RFC 4733) carries keypad digits as special RTP packets. It is far more reliable than detecting tones inside a compressed audio stream.'),
        warn('The address inside the body', 'The `c=` line contains an IP address the phone itself wrote. Behind NAT that is a private address like `192.168.1.20`, unreachable from the internet. This is the root of most one-way-audio problems and the reason media relays exist.'),
      ],
    },
    {
      id: 'rtp-rtcp',
      title: 'RTP and RTCP',
      icon: 'AudioWaveform',
      minutes: 8,
      summary: 'The packets that actually carry your voice, plus their quality reports.',
      blocks: [
        p('The **Real-time Transport Protocol** (RTP, RFC 3550) carries audio and video in small UDP packets, usually one packet every 20 ms per direction. With G.711 that is 50 packets per second each way, each carrying 160 bytes of audio.'),
        cards([
          { icon: 'Hash', title: 'Sequence number', text: 'Increments by one per packet so the receiver can detect loss and reordering.' },
          { icon: 'Timer', title: 'Timestamp', text: 'Sampling instant, in units of the codec clock rate. Drives the jitter buffer.' },
          { icon: 'Fingerprint', title: 'SSRC', text: 'Random 32-bit identifier of the media source within the session.' },
          { icon: 'Tag', title: 'Payload type', text: 'Says which codec the packet contains, matching the SDP rtpmap.' },
        ]),
        h('RTCP'),
        p('The **RTP Control Protocol** sends periodic reports about packet loss, jitter and round-trip time. Traditionally RTCP uses the next odd port (RTP on 49170, RTCP on 49171). Modern endpoints, and all WebRTC, can multiplex both on one port (`a=rtcp-mux`).'),
        h('Securing media'),
        ul([
          '**SRTP** (RFC 3711) encrypts RTP payloads.',
          '**SDES** passes the SRTP keys inside SDP (`a=crypto`). This only makes sense over TLS signaling.',
          '**DTLS-SRTP** negotiates the keys in the media path itself. WebRTC requires it.',
        ]),
        h('Quality killers'),
        table(['Problem', 'Symptom', 'Typical cause'], [
          ['Packet loss', 'Choppy, robotic audio', 'Congested links, Wi-Fi'],
          ['Jitter', 'Garbled audio, jitter buffer underruns', 'Variable queueing delay'],
          ['Latency', 'People talk over each other', 'Long paths, big jitter buffers. Aim for under 150 ms one way.'],
          ['One-way audio', 'Only one side hears', 'NAT, wrong `c=` address, firewall blocking RTP ports'],
        ]),
        info('Kamailio and RTP', 'Kamailio never sees RTP. If media must pass through your servers, Kamailio rewrites the SDP and tells an RTP relay (RTPEngine) which ports to open. You will configure this in Step 7.'),
      ],
    },
    {
      id: 'registration-flow',
      title: 'Registration with digest auth',
      icon: 'KeyRound',
      minutes: 10,
      summary: 'How a phone proves who it is and tells the registrar where to reach it.',
      blocks: [
        p('A phone sends REGISTER to announce its Contact for an AoR. A secure registrar first challenges it with a nonce, and the phone answers with a hash of its password.'),
        ladder('REGISTER with digest authentication', ['Phone', 'Registrar (Kamailio)'], [
          ['Phone', 'Registrar (Kamailio)', 'REGISTER (no credentials)', 'req'],
          ['Registrar (Kamailio)', 'Phone', '401 Unauthorized + WWW-Authenticate', 'resp'],
          ['Phone', 'Registrar (Kamailio)', 'REGISTER + Authorization', 'req'],
          ['Registrar (Kamailio)', 'Phone', '200 OK + Contact; expires=3600', 'resp'],
        ]),
        code('sip', `
SIP/2.0 401 Unauthorized
WWW-Authenticate: Digest realm="example.com", nonce="Y2Y0ZGFiMmM3N2E1", qop="auth", algorithm=MD5

REGISTER sip:example.com SIP/2.0
Authorization: Digest username="alice", realm="example.com",
  nonce="Y2Y0ZGFiMmM3N2E1", uri="sip:example.com", qop=auth,
  nc=00000001, cnonce="0a4f113b", response="6629fae49393a05397450978507c4ef1",
  algorithm=MD5
Contact: <sip:alice@192.0.2.44:52311>;expires=3600`, 'The challenge and the answer (abbreviated)'),
        h('How the response hash is computed'),
        code('text', `
HA1      = MD5(username ":" realm ":" password)
HA2      = MD5(method ":" digest-uri)
response = MD5(HA1 ":" nonce ":" nc ":" cnonce ":" qop ":" HA2)`),
        p('The password never crosses the network. The server performs the same calculation and compares. Kamailio can even store only **HA1** in its database, so the plain-text password is never needed there.'),
        h('Rules of registration'),
        ul([
          'A REGISTER refreshes before `expires` runs out. Most phones re-register at about half the interval.',
          '`Expires: 0` (or `;expires=0` on the Contact) removes a binding. `Contact: *` with `Expires: 0` removes all of them.',
          'One AoR can have several Contacts, for example a desk phone and a mobile app. Incoming calls then fork to all of them.',
          'The 200 OK lists every current binding for that AoR.',
        ]),
        tip('Try it in your head', 'If Alice registers from two devices, `lookup("location")` in Kamailio sets the first contact as the R-URI and the rest as additional branches. `t_relay()` then rings them in parallel.'),
      ],
    },
    {
      id: 'basic-call',
      title: 'A basic call through a proxy',
      icon: 'PhoneCall',
      minutes: 10,
      summary: 'The complete INVITE to BYE ladder, with authentication and record-routing.',
      blocks: [
        p('Here is a complete call from Alice to Bob through a Kamailio proxy that authenticates callers and record-routes. Read it from top to bottom.'),
        ladder('Alice calls Bob through Kamailio', ['Alice', 'Kamailio', 'Bob'], [
          ['Alice', 'Kamailio', 'INVITE sip:bob@example.com', 'req'],
          ['Kamailio', 'Alice', '407 Proxy Authentication Required', 'resp'],
          ['Alice', 'Kamailio', 'ACK', 'req'],
          ['Alice', 'Kamailio', 'INVITE + Proxy-Authorization', 'req'],
          ['Kamailio', 'Alice', '100 Trying', 'resp'],
          ['Kamailio', 'Bob', 'INVITE + Record-Route', 'req'],
          ['Bob', 'Kamailio', '180 Ringing', 'resp'],
          ['Kamailio', 'Alice', '180 Ringing', 'resp'],
          ['Bob', 'Kamailio', '200 OK (SDP answer)', 'resp'],
          ['Kamailio', 'Alice', '200 OK (SDP answer)', 'resp'],
          ['Alice', 'Kamailio', 'ACK (Route header)', 'req'],
          ['Kamailio', 'Bob', 'ACK', 'req'],
          ['Alice', 'Bob', 'RTP audio, direct', 'media'],
          ['Bob', 'Kamailio', 'BYE', 'req'],
          ['Kamailio', 'Alice', 'BYE', 'req'],
          ['Alice', 'Kamailio', '200 OK', 'resp'],
          ['Kamailio', 'Bob', '200 OK', 'resp'],
        ]),
        h('What happened at each stage'),
        ul([
          'The first INVITE has no credentials, so Kamailio challenges with 407. Alice acknowledges the failure with an ACK that ends that transaction.',
          'The second INVITE carries `Proxy-Authorization`. Kamailio verifies it, sends `100 Trying` to stop retransmissions, looks Bob up in the location table and relays the INVITE.',
          'Kamailio adds `Record-Route: <sip:203.0.113.10;lr>`, so the ACK and BYE come back through it.',
          'The 200 OK carries Bob\'s SDP answer. Alice now knows where to send RTP.',
          'Audio flows **directly** between the phones. Kamailio is not involved in the media.',
          'Either party can hang up with BYE, which follows the Route set.',
        ]),
        info('Where is the 100 Trying from?', 'When `t_relay()` creates a transaction for an INVITE, the tm module sends `100 Trying` automatically. A stateless `forward()` does not.'),
      ],
    },
    {
      id: 'cancel-hold-transfer',
      title: 'Cancel, busy, hold and transfer',
      icon: 'PhoneOff',
      minutes: 10,
      summary: 'Calls that do not go as planned, and calls that change while they are up.',
      blocks: [
        h('Caller hangs up before answer'),
        ladder('CANCEL during ringing', ['Alice', 'Kamailio', 'Bob'], [
          ['Alice', 'Kamailio', 'INVITE', 'req'],
          ['Kamailio', 'Bob', 'INVITE', 'req'],
          ['Bob', 'Kamailio', '180 Ringing', 'resp'],
          ['Kamailio', 'Alice', '180 Ringing', 'resp'],
          ['Alice', 'Kamailio', 'CANCEL', 'req'],
          ['Kamailio', 'Alice', '200 OK (to CANCEL)', 'resp'],
          ['Kamailio', 'Bob', 'CANCEL', 'req'],
          ['Bob', 'Kamailio', '200 OK (to CANCEL)', 'resp'],
          ['Bob', 'Kamailio', '487 Request Terminated', 'resp'],
          ['Kamailio', 'Bob', 'ACK', 'req'],
          ['Kamailio', 'Alice', '487 Request Terminated', 'resp'],
          ['Alice', 'Kamailio', 'ACK', 'req'],
        ]),
        p('Each hop answers the CANCEL and generates its own ACK for the 487. This is hop-by-hop behavior, and the tm module does all of it for you.'),
        h('Busy or no answer'),
        p('If Bob replies `486 Busy Here`, or does not answer before the timer expires (`408` after `fr_inv_timer`), Kamailio can catch the failure in a `failure_route` and try another destination, such as voicemail. That is serial forking, covered in Step 5.'),
        h('Hold with re-INVITE'),
        p('A re-INVITE is an INVITE sent inside an existing dialog (it has a To tag). To put Bob on hold, Alice sends a new SDP offer with a changed direction attribute:'),
        code('sdp', `
m=audio 49170 RTP/AVP 0 101
a=sendonly`, 'Alice holds: she will only send (music on hold), not receive'),
        p('Bob answers with `a=recvonly`. To resume, Alice sends another re-INVITE with `a=sendrecv`. Older devices used `c=IN IP4 0.0.0.0` instead; you still see it from legacy equipment.'),
        h('Blind transfer with REFER'),
        ul([
          'Alice is talking to Bob and wants to send Bob to Carol.',
          'Alice sends `REFER` to Bob with `Refer-To: <sip:carol@example.com>`.',
          'Bob answers `202 Accepted`, then sends a new INVITE to Carol.',
          'Bob reports progress to Alice with NOTIFY messages carrying `message/sipfrag` bodies, e.g. `SIP/2.0 200 OK`.',
          'Alice hangs up with BYE.',
        ]),
        tip('Proxies stay out of the way', 'For hold and transfer a proxy has nothing special to do. It routes the re-INVITE, REFER and NOTIFY as normal in-dialog or initial requests. The intelligence lives in the endpoints.'),
      ],
    },
    {
      id: 'nat-basics',
      title: 'NAT: SIP\'s biggest headache',
      icon: 'ShieldAlert',
      minutes: 11,
      summary: 'Why private addresses break SIP and the standard tricks used to fix it.',
      blocks: [
        p('Network Address Translation rewrites IP addresses and ports in the **packet headers**. It does not touch the SIP message text. A phone behind a home router writes its private address into Via, Contact and the SDP `c=` line, and the outside world cannot use any of those addresses.'),
        code('sip', `
INVITE sip:bob@example.com SIP/2.0
Via: SIP/2.0/UDP 192.168.1.20:5060;rport;branch=z9hG4bK-524287-1
Contact: <sip:alice@192.168.1.20:5060>

c=IN IP4 192.168.1.20
m=audio 10000 RTP/AVP 0`, 'What arrives at the server from 198.51.100.7:61324'),
        h('What breaks'),
        table(['Field', 'Problem', 'Fix'], [
          ['Via', 'Responses would go to 192.168.1.20', '`rport` (RFC 3581): server adds `received=` and `rport=` and replies to the real source.'],
          ['Contact', 'Calls to Alice and in-dialog requests are sent to a private IP', 'Server stores the real source address (`fix_nated_register()`, `fix_nated_contact()`, `set_contact_alias()`).'],
          ['SDP `c=` / `m=`', 'Bob sends RTP to a private IP: one-way or no audio', 'Rewrite SDP and relay media through a public RTP proxy.'],
          ['Pinhole lifetime', 'Router forgets the UDP mapping after 30-60 s, incoming calls fail', 'Keepalives: CRLF pings, OPTIONS pings (`nathelper`), shorter registration intervals.'],
        ]),
        h('Detecting NAT'),
        p('A server can tell a client is behind NAT when the address in the SIP message differs from the address the packet came from, or when it is an RFC 1918 private address (10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16) or the CGNAT range 100.64.0.0/10. Kamailio\'s `nat_uac_test()` performs exactly these checks.'),
        h('Other tools you will hear about'),
        cards([
          { icon: 'Compass', title: 'STUN', text: 'Lets a client discover its own public address. Helps some NAT types, but not symmetric NAT.' },
          { icon: 'Repeat2', title: 'TURN', text: 'A relay server for media. Always works, costs bandwidth.' },
          { icon: 'Snowflake', title: 'ICE', text: 'Tries every candidate address (host, STUN, TURN) and picks the best working pair. Mandatory in WebRTC.' },
          { icon: 'ShieldOff', title: 'SIP ALG', text: 'A router feature that rewrites SIP. Usually buggy. Disable it on customer routers.' },
        ]),
        warn('Disable SIP ALG', 'Many consumer routers ship with a SIP Application Layer Gateway that rewrites packets badly. When a customer has strange registration or audio problems, turning off SIP ALG is the first thing to try.'),
      ],
    },
  ],
  quiz: [
    { q: 'Which SDP line contains the IP address where a phone wants to receive media?', options: ['o=', 'c=', 's=', 'v='], answer: 1, explain: 'The connection line c= holds the receive address. m= holds the port.' },
    { q: 'What does an endpoint send to put the other party on hold?', options: ['A CANCEL', 'A BYE', 'A re-INVITE with a=sendonly', 'A REGISTER with Expires: 0'], answer: 2, explain: 'Hold is a re-INVITE with a new SDP offer whose direction attribute is sendonly (or inactive).' },
    { q: 'In digest authentication, what does HA1 contain?', options: ['The plain password', 'MD5(username:realm:password)', 'The nonce', 'MD5(method:uri)'], answer: 1, explain: 'HA1 hashes username, realm and password. Kamailio can store just HA1 instead of the password.' },
    { q: 'After a CANCEL succeeds, which final response does the original INVITE receive?', options: ['200 OK', '408 Request Timeout', '486 Busy Here', '487 Request Terminated'], answer: 3, explain: '487 Request Terminated tells the caller the INVITE was cancelled.' },
    { q: 'Which RFC 3581 Via parameter tells the server to reply to the packet\'s real source port?', options: ['branch', 'rport', 'lr', 'maddr'], answer: 1, explain: 'With rport the server fills in received and rport and sends responses back through the NAT mapping.' },
  ],
};

export default course;
